import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/pages/_home.scss';

import HeroSection from './home/sections/HeroSection';
import MarqueeTicker from './home/sections/MarqueeTicker';
import RecentProjects from './home/sections/RecentProjects';
import ProfileBlock, { ProfileBlockTechIcons } from './home/sections/ProfileBlock';
import InlineAboutSection from './home/sections/InlineAboutSection';
import ContactCTA from './home/sections/ContactCTA';
import CharacterSelectorSection from './home/sections/CharacterSelectorSection';
import { useSecretSequence } from '../hooks/useSecretSequence';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { CommandPalette } from '../game/CommandPalette';
import { useGameContext } from '../game/GameContext';
import { primeAudio } from '../game/audio';

const TechConstellation = React.lazy(() => import('./home/sections/TechConstellation'));

const GLITCH_DURATION_MS = 600;
const GLITCH_SAFETY_TIMEOUT_MS = 2000;
const HINT_FADE_DELAY_MS = 3000;

const Home: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isGlitching, setIsGlitching] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isPaletteOpen, setIsPaletteOpen] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const { isActive, activate, exitGame } = useGameContext();

  // D-12: ref guard so rapid re-triggers no-op while a glitch is in-flight
  const glitchingRef = useRef<boolean>(false);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Trigger initial load animations
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // D-08: hint fades in - we gate rendering by a 3s delay so it never blocks above-the-fold paint
  useEffect(() => {
    const timer = setTimeout(() => setShowHint(true), HINT_FADE_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // D-11 + SECRET-01: handler wraps double-trigger guard + reduced-motion branch
  const handleSecretMatch = useCallback((): void => {
    // D-11 route suppression: only active on homepage
    if (location.pathname !== '/') return;
    // D-12: already glitching - no-op
    if (glitchingRef.current) return;

    glitchingRef.current = true;

    // D-04: reduced-motion - skip animation entirely and navigate immediately
    if (prefersReducedMotion) {
      navigate('/secret');
      glitchingRef.current = false;
      return;
    }

    setIsGlitching(true);

    // Normal path: wait GLITCH_DURATION_MS, then navigate
    animationTimerRef.current = setTimeout(() => {
      navigate('/secret');
      setIsGlitching(false);
      glitchingRef.current = false;
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
    }, GLITCH_DURATION_MS);

    // D-05: 2000ms safety timeout - clears overlay and navigates regardless
    safetyTimerRef.current = setTimeout(() => {
      navigate('/secret');
      setIsGlitching(false);
      glitchingRef.current = false;
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }
    }, GLITCH_SAFETY_TIMEOUT_MS);
  }, [location.pathname, prefersReducedMotion, navigate]);

  // SECRET-01: attach listener unconditionally (hook is mounted) - handler gates by pathname (D-11)
  useSecretSequence('secret', handleSecretMatch);

  // Phase 39 — 'cnf' opens the command palette on the homepage only.
  // Implemented as a direct useEffect (not useSecretSequence) to avoid hook
  // ordering issues introduced by restoring this code between other hook calls.
  const isPaletteOpenRef = useRef(isPaletteOpen);
  const isActiveRef = useRef(isActive);
  useEffect(() => { isPaletteOpenRef.current = isPaletteOpen; }, [isPaletteOpen]);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  useEffect(() => {
    const TARGET = 'cnf';
    const buf: string[] = [];
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    const handler = (e: KeyboardEvent): void => {
      const active = document.activeElement;
      if (active && active.matches('input, textarea, select, [contenteditable]')) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.key.length !== 1) return;
      buf.push(e.key.toLowerCase());
      if (buf.length > TARGET.length) buf.shift();
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => { buf.length = 0; }, 2000);
      if (buf.join('') === TARGET) {
        buf.length = 0;
        if (location.pathname === '/' && !isPaletteOpenRef.current && !isActiveRef.current) {
          setIsPaletteOpen(true);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [location.pathname]); // only re-register when pathname changes

  // Phase 39 — safety: if the game becomes active for any reason, ensure the palette is closed
  useEffect(() => {
    if (isActive && isPaletteOpen) {
      setIsPaletteOpen(false);
    }
  }, [isActive, isPaletteOpen]);

  // Cleanup any in-flight timers on unmount
  useEffect(() => {
    return () => {
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, []);

  // SECRET-03 + D-10: only render hint on homepage
  const isHomePage = location.pathname === '/';

  return (
    <>
      <div className="home-container">
        <div className="content-wrapper">
          <div className={`left-section ${isLoaded ? 'animate-in' : ''}`}>
            <HeroSection isLoaded={isLoaded} />
          </div>

          <ProfileBlock isLoaded={isLoaded} />
        </div>

        <ProfileBlockTechIcons isLoaded={isLoaded} />
      </div>

      <MarqueeTicker isLoaded={isLoaded} />
      <RecentProjects isLoaded={isLoaded} />
      <InlineAboutSection isLoaded={isLoaded} />
      <Suspense fallback={null}>
        <TechConstellation isLoaded={isLoaded} />
      </Suspense>
      <ContactCTA isLoaded={isLoaded} />
      <CharacterSelectorSection isLoaded={isLoaded} />

      {/* SECRET-03: hint - only on homepage, only after 3s delay */}
      {isHomePage && showHint && (
        <div className="secret-hint" aria-hidden="true">
          &gt; type &quot;secret&quot; to enter the void<span className="secret-hint-cursor">_</span>
        </div>
      )}

      {/* SECRET-02 + D-06: glitch overlay - portal-less, rendered in Home tree */}
      {isGlitching && (
        <div
          className="secret-glitch-overlay"
          role="status"
          aria-live="assertive"
        >
          <span className="visually-hidden" style={{ position: 'absolute', left: '-9999px' }}>
            Entering the void
          </span>
        </div>
      )}

      {/* Phase 39 — command palette overlay (FOUND-04, FOUND-05, FOUND-06) */}
      {isPaletteOpen && (
        <CommandPalette
          onClose={() => setIsPaletteOpen(false)}
          onActivate={() => { primeAudio(); activate(); }}
        />
      )}

      {/* Phase 39 — SAFE-01: DOM stop-game button (top-right, pointer-events: auto) */}
      {isActive && (
        <button
          type="button"
          className="game-stop-btn"
          onClick={() => exitGame()}
          aria-label="Stop game"
        >
          ✕
        </button>
      )}
    </>
  );
};

export default Home;
